/**
 * 客户端缓存工具
 *
 * 提供内存缓存和localStorage缓存，支持过期时间设置
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresAt?: number;
}

interface CacheOptions {
  /** 过期时间（毫秒） */
  ttl?: number;
  /** 缓存键前缀 */
  prefix?: string;
}

/**
 * 内存缓存类（页面刷新后清空）
 */
export class MemoryCache {
  private cache = new Map<string, CacheItem<any>>();
  private defaultTTL = 5 * 60 * 1000; // 5分钟默认过期时间

  /**
   * 设置缓存
   */
  set<T>(key: string, data: T, options: CacheOptions = {}): void {
    const { ttl = this.defaultTTL } = options;
    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: ttl > 0 ? Date.now() + ttl : undefined
    };
    this.cache.set(key, item);
  }

  /**
   * 获取缓存
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key) as CacheItem<T> | undefined;

    if (!item) {
      return null;
    }

    // 检查是否过期
    if (item.expiresAt && Date.now() > item.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return item.data;
  }

  /**
   * 删除缓存
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * 清空所有缓存
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * 获取缓存统计信息
   */
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

/**
 * LocalStorage缓存类（持久化存储）
 */
export class LocalStorageCache {
  private prefix: string;

  constructor(prefix = 'app_cache_') {
    this.prefix = prefix;
  }

  private getKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  /**
   * 设置缓存到localStorage
   */
  set<T>(key: string, data: T, options: CacheOptions = {}): void {
    try {
      const { ttl = 30 * 60 * 1000 } = options; // 默认30分钟
      const item: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        expiresAt: ttl > 0 ? Date.now() + ttl : undefined
      };
      const serialized = JSON.stringify(item);
      localStorage.setItem(this.getKey(key), serialized);
    } catch (error) {
      console.warn('Failed to set cache in localStorage:', error);
    }
  }

  /**
   * 从localStorage获取缓存
   */
  get<T>(key: string): T | null {
    try {
      const serialized = localStorage.getItem(this.getKey(key));
      if (!serialized) {
        return null;
      }

      const item: CacheItem<T> = JSON.parse(serialized);

      // 检查是否过期
      if (item.expiresAt && Date.now() > item.expiresAt) {
        this.delete(key);
        return null;
      }

      return item.data;
    } catch (error) {
      console.warn('Failed to get cache from localStorage:', error);
      // 解析失败时清理缓存
      this.delete(key);
      return null;
    }
  }

  /**
   * 删除localStorage缓存
   */
  delete(key: string): void {
    try {
      localStorage.removeItem(this.getKey(key));
    } catch (error) {
      console.warn('Failed to delete cache from localStorage:', error);
    }
  }

  /**
   * 清空所有缓存
   */
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear cache from localStorage:', error);
    }
  }
}

/**
 * 首页数据缓存管理器
 */
export class VideoListCache {
  private memoryCache = new MemoryCache();
  private localCache = new LocalStorageCache('video_list_');

  private readonly CACHE_KEY_PREFIX = 'videos_';
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5分钟缓存

  /**
   * 生成缓存键
   */
  private getCacheKey(page: number, search: string, languageFilter: string): string {
    return `${this.CACHE_KEY_PREFIX}p${page}_s${search || 'all'}_l${languageFilter || 'all'}`;
  }

  /**
   * 设置首页数据缓存
   */
  set(page: number, search: string, languageFilter: string, data: any): void {
    const key = this.getCacheKey(page, search, languageFilter);

    // 内存缓存（快速访问）
    this.memoryCache.set(key, data, { ttl: this.CACHE_TTL });

    // localStorage缓存（页面刷新后仍然可用）
    this.localCache.set(key, data, { ttl: this.CACHE_TTL });
  }

  /**
   * 获取首页数据缓存
   */
  get(page: number, search: string, languageFilter: string): any | null {
    const key = this.getCacheKey(page, search, languageFilter);

    // 先检查内存缓存
    let data = this.memoryCache.get(key);
    if (data) {
      console.log(`[Cache] 命中内存缓存: ${key}`);
      return data;
    }

    // 再检查localStorage缓存
    data = this.localCache.get(key);
    if (data) {
      console.log(`[Cache] 命中本地缓存: ${key}`);
      // 同步到内存缓存
      this.memoryCache.set(key, data, { ttl: this.CACHE_TTL });
      return data;
    }

    return null;
  }

  /**
   * 清空所有缓存
   */
  clear(): void {
    this.memoryCache.clear();
    this.localCache.clear();
  }

  /**
   * 清除特定页面的缓存
   */
  clearPage(page: number): void {
    // 这里简化处理，实际可以遍历所有相关的key
    // 由于缓存key包含页面信息，这里暂时清空所有缓存
    this.clear();
  }

  /**
   * 获取缓存统计信息
   */
  getStats() {
    return {
      memory: this.memoryCache.getStats(),
      localStorage: '无法直接统计' // localStorage统计需要额外实现
    };
  }
}

// 创建全局实例
export const videoListCache = new VideoListCache();

// 导出工具函数
export const memoryCache = new MemoryCache();
export const localStorageCache = new LocalStorageCache();
